import { z } from 'zod';
import { compareDates } from './dates';
import {
  Priorities,
  PRIORITY_RANK,
  type Priority,
  PROJECT_STATUS_RANK,
  type ProjectStatus,
  ProjectStatuses,
} from './enums';
import {
  dateSchema,
  optionalDate,
  optionalNumber,
  optionalUrl,
  queryList,
  requiredDate,
  requiredText,
  versionField,
} from './fields';
import { compareText } from './format';
import { idSchema } from './ids';
import { listQuerySchema } from './list';

/** "abc-1, DEF-2 ,," → ["ABC-1", "DEF-2"]: trimmed, upper-cased, de-duplicated (FR-PRJ-006). */
const jiraKeyList = (pattern: RegExp, message: string) =>
  z
    .union([z.string(), z.array(z.string())])
    .transform((v) => [
      ...new Set((Array.isArray(v) ? v : v.split(',')).map((k) => k.trim().toUpperCase()).filter(Boolean)),
    ])
    // Checked as a whole so the error belongs to the form field, not to an array element.
    .pipe(
      z
        .array(z.string())
        .max(20)
        .refine((keys) => keys.every((k) => pattern.test(k)), { message }),
    )
    .optional();

export const JIRA_PROJECT_KEY = /^[A-Z][A-Z0-9_]{1,19}$/;
export const JIRA_ISSUE_KEY = /^[A-Z][A-Z0-9_]{1,19}-\d{1,9}$/;

/** FR-PRJ-001…007. */
export const projectInputSchema = z.object({
  name: requiredText(200, 'Projekta nosaukums ir obligāts'),
  customerId: z.string().min(1, { message: 'Izvēlieties klientu' }).pipe(idSchema),
  status: ProjectStatuses.schema,
  priority: Priorities.schema,
  startDate: requiredDate('Sākuma datums ir obligāts'),
  plannedFinish: requiredDate('Plānotās beigas ir obligātas'),
  actualFinish: optionalDate,
  /** 0–100, whole number; empty = not specified (FR-PRJ-004). */
  completion: optionalNumber({ min: 0, max: 100, int: true }),
  /** Defaults to on (FR-PRJ-005) — one default for every channel. */
  financialManagement: z.boolean().default(true),
  jiraProjectKeys: jiraKeyList(JIRA_PROJECT_KEY, 'Jira projekta atslēga, piem. "ABC"'),
  jiraEpicKeys: jiraKeyList(JIRA_ISSUE_KEY, 'Jira epika atslēga, piem. "ABC-12"'),
  charterUrl: optionalUrl,
});
export type ProjectInput = z.input<typeof projectInputSchema>;

export const projectUpdateSchema = projectInputSchema.extend(versionField);

export const projectSchema = z.object({
  id: idSchema,
  name: z.string(),
  customerId: idSchema,
  customerName: z.string(),
  status: ProjectStatuses.schema,
  priority: Priorities.schema,
  startDate: dateSchema,
  plannedFinish: dateSchema,
  actualFinish: dateSchema.nullable(),
  completion: z.number().int().nullable(),
  financialManagement: z.boolean(),
  jiraProjectKeys: z.array(z.string()),
  jiraEpicKeys: z.array(z.string()),
  charterUrl: z.string().nullable(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Project = z.infer<typeof projectSchema>;

export const PROJECT_SORT_FIELDS = [
  'name',
  'customer',
  'status',
  'startDate',
  'plannedFinish',
  'priority',
  'completion',
  'updatedAt',
] as const;
export type ProjectSortField = (typeof PROJECT_SORT_FIELDS)[number];

/** Dashboard list (FR-DASH-003/004/006): search, status set (empty = all), sort. */
export const projectListQuerySchema = listQuerySchema.extend({
  sort: z.enum(PROJECT_SORT_FIELDS).default('name'),
  statuses: queryList(ProjectStatuses.schema),
  customerId: idSchema.optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(1000),
});
export type ProjectListQuery = z.infer<typeof projectListQuerySchema>;

/**
 * Sort semantics of the project list (FR-DASH-007): status and priority by rank, dates
 * chronologically with empty first, completion numerically with empty = 0, text A→Z ignoring
 * case. Ties keep a stable order by name, then id.
 */
export function compareProjects(field: ProjectSortField, a: Project, b: Project): number {
  const primary = (() => {
    switch (field) {
      case 'status':
        return (
          PROJECT_STATUS_RANK[a.status as ProjectStatus] - PROJECT_STATUS_RANK[b.status as ProjectStatus]
        );
      case 'priority':
        return PRIORITY_RANK[a.priority as Priority] - PRIORITY_RANK[b.priority as Priority];
      case 'startDate':
      case 'plannedFinish':
        return compareDates(a[field], b[field]);
      case 'updatedAt':
        return compareDates(a.updatedAt, b.updatedAt);
      case 'completion':
        return (a.completion ?? 0) - (b.completion ?? 0);
      case 'customer':
        return compareText(a.customerName, b.customerName);
      case 'name':
        return compareText(a.name, b.name);
    }
  })();
  return primary || compareText(a.name, b.name) || (BigInt(a.id) < BigInt(b.id) ? -1 : 1);
}

/** Projects whose card offers "add task": open ones first, closed grouped (FR-TASK-016). */
export const projectOptionSchema = z.object({
  id: idSchema,
  name: z.string(),
  customerId: idSchema,
  customerName: z.string(),
  status: ProjectStatuses.schema,
});
export type ProjectOption = z.infer<typeof projectOptionSchema>;
